import { sleep } from "lib/utils"
import { profilesMockData } from "mock/datas/profiles"
import { useState, useCallback } from "react"
import { useAuth } from "services/auth/auth-service-hooks"
import { ControllerMockOutput, FetchMockProps } from "types/mms-base"

const defaultControllerFunctionOutput: ControllerMockOutput = {
    success: true,
    status: 200,
    error: null,
    data: null,
}

const defaultControllerFunction: () => ControllerMockOutput = () => defaultControllerFunctionOutput

export const useFetchMock = ({
    mockStatus = 200,
    delay = 200,
    controllerFn = defaultControllerFunction
}: FetchMockProps): FetchApiProps => {
    const [data, setData] = useState<string | number | boolean | object | null>(null)
    const [success, setSuccess] = useState<boolean | null>(null)
    const [status, setStatus] = useState<null | number>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<object | null>(null)
    const { isAuthenticated, getAccessTokenSilently } = useAuth()

    const sendRequest = useCallback(
        async (body: object | null = null) => {
            setIsLoading(true)
            setError(null)

            try {
                await sleep(delay)
                const token = await getAccessTokenSilently()
                const user =
                    profilesMockData.find((p) => p.id === token) || null
                if (!isAuthenticated || !user) {
                    setStatus(401)
                    throw new Error("User not authenticated") as object
                }
                const response = controllerFn({ body, user })
                const { status: responseStatus = mockStatus } =
                    response || defaultControllerFunctionOutput
                const { data: responseData = null } =
                    response || defaultControllerFunctionOutput
                const { error: responseError } =
                    response || defaultControllerFunctionOutput
                setStatus(responseStatus)
                if (responseError) {
                    throw new Error(responseError) as object
                }
                setData(responseData)
                setSuccess(true)
            } catch (err) {
                setSuccess(false)
                setError(err as object)
            } finally {
                setIsLoading(false)
            }
        },
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            getAccessTokenSilently,
            isAuthenticated,
        ]
    )

    return { data, isLoading, success, error, status, sendRequest }
}
